import { useMemo } from "react";
import { Link } from "react-router";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";

import { useAuthUser, useUserRoles } from "@/features/auth";
import { ArrowRight } from "lucide-react";
import { LinkedinIcon as Linkedin } from "@/components/icons/LinkedinIcon";
import { useLenis } from "@/components/providers/lenisHooks";

export const Footer = () => {
  const { user } = useAuthUser();
  const { isAdmin, isEmployer } = useUserRoles(user?.id);
  const lenis = useLenis();

  const scrollToTop = () => {
    if (lenis) lenis.scrollTo(0, { duration: 1.2 });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const candidateProfileLink = user ? "/profile" : "/archive";
  const partnerLink = !user
    ? "/archive"
    : isAdmin
    ? "/admin"
    : isEmployer
    ? "/employer"
    : "/employer/access";
  const postRoleLink = isEmployer || isAdmin ? "/post-job" : partnerLink;
  const talentSearchLink = isEmployer || isAdmin ? "/employer/candidates" : partnerLink;

  const footerSections = useMemo(
    () => [
      {
        title: "For candidates",
        links: [
          { label: "Browse open roles", to: "/jobs" },
          { label: "Critical environment roles", to: "/jobs?category=ce_facilities" },
          { label: "AI tools", to: "/premium" },
          { label: "Career services", to: "/services" },
          { label: "How it works", to: "/how-it-works" },
          { label: user ? "Dashboard" : "Archive notice", to: user ? "/dashboard" : "/archive" },
          { label: user ? "Your profile" : "Sign in", to: user ? candidateProfileLink : "/auth" },
          { label: "Talent Wall", to: "/talent" },
        ],
      },
      {
        title: "For employers",
        links: [
          { label: "Employer portal", to: partnerLink },
          { label: "Post a role", to: postRoleLink },
          { label: "Talent search", to: talentSearchLink },
          { label: "Employer solutions", to: "/employers" },
          { label: "Company profiles", to: "/companies" },
        ],
      },
      {
        title: "Resources",
        links: [
          { label: "About", to: "/about" },
          { label: "Press & media", to: "/press" },
          { label: "Contact", to: "/contact" },
          { label: "Learning hub", to: "/learning" },
          { label: "Course catalog", to: "/learning/courses" },
          { label: "Ideas hub", to: "/ideas" },
          { label: "How it was built", to: "/archive/how-it-worked" },
          { label: "Glossary", to: "/signal/glossary" },
          { label: "FAQ", to: "/faq" },
        ],
      },
      {
        title: "Legal",
        links: [
          { label: "Privacy policy", to: "/privacy" },
          { label: "Terms of use", to: "/terms" },
          { label: "AI transparency", to: "/legal/ai-transparency" },
          { label: "Cookie policy", to: "/legal/cookies" },
          { label: "Acceptable use", to: "/legal/acceptable-use" },
          { label: "Security", to: "/security" },
          { label: "Subprocessors", to: "/subprocessors" },
          { label: "Disclaimer", to: "/disclaimer" },
        ],
      },
    ],
     
    [candidateProfileLink, partnerLink, postRoleLink, talentSearchLink, user]
  );

  return (
    <footer className="bg-card mt-auto relative border-t border-border/60 pb-[env(safe-area-inset-bottom)]">
      <div className="container mx-auto px-4 py-10 sm:py-12 lg:py-16">
        {/* Brand row */}
        <div className="mb-9 flex flex-col gap-4 sm:mb-12 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <Logo className="footer-logo" />
            <p className="text-xs text-muted-foreground max-w-md sm:text-sm">
              Purpose-built hiring infrastructure for AI, data center, and critical environment professionals.
              Archived August 18, 2026 — read-only.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {!user && (
              <Link to="/archive" onClick={scrollToTop}>
                <Button size="sm" variant="premium" className="h-9 px-4 rounded-md gap-1.5">
                  Archive notice
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </Link>
            )}
            <a
              href="https://www.linkedin.com/company/rackandrole"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Rack & Role on LinkedIn"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border/60 text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
            >
              <Linkedin className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* Link columns — single column mobile, 2-up tablet, 4-up desktop */}
        <div className="grid grid-cols-1 gap-x-8 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
          {footerSections.map((section) => (
            <div key={section.title} className="min-w-0">
              <h3 className="mono-eyebrow mb-3 text-[11px]">{section.title}</h3>
              <ul className="space-y-2 text-sm">
                {section.links.map((link) => (
                  <li key={`${section.title}-${link.label}`}>
                    <Link
                      to={link.to}
                      onClick={scrollToTop}
                      className="text-muted-foreground hover:text-primary transition-colors duration-200 inline-block break-words hover:translate-x-0.5"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="mt-10 flex flex-col items-start justify-between gap-3 border-t border-border/50 pt-5 text-xs text-muted-foreground sm:mt-12 sm:flex-row sm:items-center sm:gap-4">
          <p>&copy; {new Date().getFullYear()} Rack & Role. All rights reserved.</p>
          <p className="font-mono uppercase tracking-[0.28em] text-[10px] inline-flex items-center gap-2">
            <span className="text-muted-foreground">designed by</span>
            <a
              href="https://frankwilson.dev"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Frank S. Wilson — designer portfolio (frankwilson.dev)"
              className="group relative inline-flex items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(28_92%_56%)/0.6] focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -inset-x-2 -inset-y-1 rounded-sm opacity-0 blur-md transition-opacity duration-500 group-hover:opacity-70"
                style={{
                  background:
                    "radial-gradient(60% 80% at 50% 50%, hsl(28 92% 56% / 0.55), transparent 70%)",
                }}
              />
              <span
                aria-hidden="true"
                className="relative inline-block h-7 w-[72px] transition-transform duration-500 group-hover:scale-[1.04]"
                style={{
                  backgroundImage:
                    "linear-gradient(92deg, hsl(28 92% 56%) 0%, hsl(20 95% 60%) 35%, hsl(340 80% 60%) 75%, hsl(262 80% 62%) 100%)",
                  WebkitMaskImage: "url(/fsw-signature.png)",
                  maskImage: "url(/fsw-signature.png)",
                  WebkitMaskRepeat: "no-repeat",
                  maskRepeat: "no-repeat",
                  WebkitMaskPosition: "center",
                  maskPosition: "center",
                  WebkitMaskSize: "contain",
                  maskSize: "contain",
                  filter: "drop-shadow(0 0 6px hsl(28 92% 56% / 0.35))",
                }}
              />
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
